import { useEffect, useState } from "react";
import { Link, NavLink, Outlet } from "react-router-dom";
import { Menu, ShoppingBag, User, Instagram, Facebook, MapPin, Phone, Mail, Clock } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "../ui/sheet";
import { Logo } from "../brand/Logo";
import { useAuth } from "../../context/AuthContext";
import { cartCount } from "../../lib/cart";
import { api } from "../../lib/api";
import { MaintenancePage, useMaintenanceMode } from "../../lib/maintenance";

const NAV = [
  { to: "/", label: "Accueil" },
  { to: "/boutique", label: "Location" },
  { to: "/coordination", label: "Coordination" },
  { to: "/realisations", label: "Réalisations" },
  { to: "/lieux", label: "Lieux" },
  { to: "/mon-histoire", label: "Mon histoire" },
  { to: "/blog", label: "Blog" },
  { to: "/contact", label: "Contact" },
];

const Header = () => {
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const count = cartCount();
  return (
    <header className="sticky top-0 z-40 border-b border-border bg-cream/80 backdrop-blur-xl" data-testid="public-header">
      <div className="container-ae flex h-18 items-center justify-between gap-4 py-4">
        <Link to="/" aria-label="Art Event — Accueil" data-testid="header-logo-link">
          <img src="/assets/logo-art-event.png" alt="ART EVENT — Créateur d'émotions, mariages, réceptions, événements" className="h-14 w-auto md:h-16" data-testid="brand-logo" />
        </Link>
        <nav className="hidden lg:flex items-center gap-7" aria-label="Navigation principale">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={({ isActive }) =>
                `text-[13px] font-semibold uppercase tracking-[0.12em] transition-colors ${isActive ? "text-deepgreen" : "text-foreground/60 hover:text-deepgreen"}`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          <Link to="/ma-selection" data-testid="header-selection-link" className="relative rounded-full p-2.5 text-deepgreen transition-colors hover:bg-sage-light" aria-label="Ma sélection">
            <ShoppingBag className="h-5 w-5" />
            {count > 0 && (
              <span className="absolute -right-0.5 -top-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-gold text-[10px] font-bold text-deepgreen" data-testid="header-cart-count">{count}</span>
            )}
          </Link>
          <Link to={user ? "/compte" : "/connexion"} data-testid="header-account-link" className="rounded-full p-2.5 text-deepgreen transition-colors hover:bg-sage-light" aria-label="Mon compte">
            <User className="h-5 w-5" />
          </Link>
          <Link to="/rendez-vous" data-testid="header-cta-appointment" className="btn-primary-ae hidden md:inline-flex !px-5 !py-2.5 !text-xs">
            Prendre rendez-vous
          </Link>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button className="rounded-full p-2.5 text-deepgreen lg:hidden" data-testid="header-mobile-menu-button" aria-label="Ouvrir le menu">
                <Menu className="h-6 w-6" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-cream w-80">
              <nav className="mt-8 flex flex-col gap-1" aria-label="Navigation mobile">
                {NAV.map((item) => (
                  <Link
                    key={item.to}
                    to={item.to}
                    onClick={() => setOpen(false)}
                    data-testid={`mobile-nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className="rounded-lg px-4 py-3 text-sm font-semibold uppercase tracking-[0.12em] text-deepgreen hover:bg-sage-light"
                  >
                    {item.label}
                  </Link>
                ))}
                <Link to="/rendez-vous" onClick={() => setOpen(false)} data-testid="mobile-nav-appointment" className="btn-primary-ae mt-4">
                  Prendre rendez-vous
                </Link>
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

const Footer = () => {
  const [social, setSocial] = useState(null);
  useEffect(() => {
    api.get("/site-settings").then((r) => setSocial(r.data)).catch(() => setSocial({}));
  }, []);
  return (
  <footer className="bg-deepgreen text-cream" data-testid="public-footer">
    <div className="container-ae grid gap-12 py-16 md:grid-cols-2 lg:grid-cols-4">
      <div>
        <Logo dark />
        <p className="mt-4 text-sm leading-relaxed text-cream/70">
          Location de décoration, mobilier et jeux en bois. Coordination de mariage. Artres, Nord.
        </p>
        {(social?.instagram_url || social?.facebook_url) && (
          <div className="mt-5 flex gap-3">
            {social.instagram_url && (
              <a href={social.instagram_url} target="_blank" rel="noreferrer" data-testid="footer-instagram-link" aria-label="Instagram" className="rounded-full border border-cream/20 p-2.5 transition-colors hover:bg-cream/10">
                <Instagram className="h-4 w-4" />
              </a>
            )}
            {social.facebook_url && (
              <a href={social.facebook_url} target="_blank" rel="noreferrer" data-testid="footer-facebook-link" aria-label="Facebook" className="rounded-full border border-cream/20 p-2.5 transition-colors hover:bg-cream/10">
                <Facebook className="h-4 w-4" />
              </a>
            )}
          </div>
        )}
      </div>
      <div>
        <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-sage">Location</h4>
        <ul className="mt-4 space-y-2.5 text-sm text-cream/80">
          <li><Link to="/boutique" className="hover:text-cream" data-testid="footer-link-catalogue">La collection</Link></li>
          <li><Link to="/boutique/arches-et-structures" className="hover:text-cream" data-testid="footer-link-arches">Arches et structures</Link></li>
          <li><Link to="/boutique/mobilier" className="hover:text-cream" data-testid="footer-link-mobilier">Mobilier</Link></li>
          <li><Link to="/boutique/jeux-en-bois" className="hover:text-cream" data-testid="footer-link-jeux">Jeux en bois</Link></li>
          <li><Link to="/ma-selection" className="hover:text-cream" data-testid="footer-link-selection">Ma sélection</Link></li>
        </ul>
      </div>
      <div>
        <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-sage">Art Event</h4>
        <ul className="mt-4 space-y-2.5 text-sm text-cream/80">
          <li><Link to="/coordination" className="hover:text-cream" data-testid="footer-link-coordination">Coordination de mariage</Link></li>
          <li><Link to="/realisations" className="hover:text-cream" data-testid="footer-link-realisations">Réalisations</Link></li>
          <li><Link to="/partenaires" className="hover:text-cream" data-testid="footer-link-partenaires">Partenaires</Link></li>
          <li><Link to="/mon-histoire" className="hover:text-cream" data-testid="footer-link-histoire">Mon histoire</Link></li>
          <li><Link to="/blog" className="hover:text-cream" data-testid="footer-link-blog">Blog &amp; conseils</Link></li>
        </ul>
      </div>
      <div>
        <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-sage">Contact</h4>
        <ul className="mt-4 space-y-3 text-sm text-cream/80">
          <li className="flex items-center gap-2.5"><MapPin className="h-4 w-4 text-sage" /> Artres (Nord) — livraison jusqu'à 80 km</li>
          <li className="flex items-center gap-2.5"><Phone className="h-4 w-4 text-sage" /> 06 88 60 83 53</li>
          <li className="flex items-center gap-2.5"><Mail className="h-4 w-4 text-sage" /> contact@art-events.fr</li>
          <li className="flex items-center gap-2.5"><Clock className="h-4 w-4 text-sage" /> Lundi au samedi, 9 h – 18 h</li>
        </ul>
      </div>
    </div>
    <div className="border-t border-cream/10">
      <div className="container-ae flex flex-col items-center justify-between gap-3 py-6 text-xs text-cream/50 md:flex-row">
        <p>© 2026 Art Event — art-events.fr</p>
        <div className="flex flex-wrap justify-center gap-x-6 gap-y-2">
          <Link to="/cgv" data-testid="footer-link-cgv" className="hover:text-cream/80">Conditions Générales de Vente</Link>
          <Link to="/mentions-legales" data-testid="footer-link-mentions-legales" className="hover:text-cream/80">Mentions légales</Link>
          <Link to="/politique-confidentialite" data-testid="footer-link-confidentialite" className="hover:text-cream/80">Politique de confidentialité</Link>
          <Link to="/politique-cookies" data-testid="footer-link-cookies" className="hover:text-cream/80">Politique relative aux cookies</Link>
        </div>
      </div>
    </div>
  </footer>
  );
};

export const PublicLayout = () => {
  const maintenance = useMaintenanceMode();
  if (maintenance) {
    return (
      <div className="flex min-h-screen flex-col bg-cream">
        <main className="flex-1"><MaintenancePage /></main>
      </div>
    );
  }
  return (
  <div className="flex min-h-screen flex-col bg-cream">
    <Header />
    <main className="flex-1">
      <Outlet />
    </main>
    <Footer />
  </div>
  );
};
